// @catalog-bitcash-code/format — the Bitcash v2 FORMAT PACK, byte level
// ============================================================================
// apps/bitcash/docs/plans/bitcash-v2.md §4.21 (normative; milestone F, re-cut at F′ and frozen).
// Everything a ledger row, a reference, an identifier or a request signature
// is made of, as bytes:
//
//   §4.21.1  lp, hex, base64url, base32, network ids, decimals, days, and the
//            encodings of country, payerKind, serviceType, category, tier, assetId
//   §4.21.3  view code, ref, role refs, site refs
//   §4.21.4  the canonical row string C (18 fields), its signing input and Merkle leaf
//   §4.21.5  the row-type registry, as data, and its rules over a row's fields
//   §4.21.8  intent keys          §4.21.9  sale and order ids
//   §4.21.10 kid, signing inputs  §4.21.11 the request envelope
//   §4.21.13 content secrets and the content URL digest
//   §4.21.16 batchId and the session identifier tabId
//
// The log hashes of §4.21.14, the added-column rule of `sum` and the usage
// export's leaves and signing input are in ./log.ts (the one module with a
// dependency: canonical JSON). The C# twin is libs/bitcash-code/*.cs; both are
// pinned by libs/bitcash-code/vectors/format-pack.json, written by the
// independent python generator beside it.
//
// Browser-safe and dependency-free like ./index.ts: hash functions are passed
// in by the host (`sha256`, `hmacSha256`: noble in the browser, node:crypto in
// the Worker and the tools), synchronous, and nothing here imports from node.
//
// Conventions of this module: a REF is returned as its 64 lowercase hex
// characters (what rows, types and the wire carry); every other hash — a view
// digest, an intent key, a batch id, a chain, a secret — as raw bytes, for the
// caller to hex where the format says so. Integers are taken as decimal
// strings, safe integers or bigints; nothing above 2^53 passes through a number.

import { BODY_CHARS, BODY_LENGTH, TYPE_VIEW, codeType, composeCompact, toCompact } from './index';

/** A synchronous SHA-256, supplied by the host. */
export type Sha256 = (data: Uint8Array) => Uint8Array;
/** A synchronous HMAC-SHA256, supplied by the host. */
export type HmacSha256 = (key: Uint8Array, data: Uint8Array) => Uint8Array;

// ── §4.21.1 conventions ─────────────────────────────────────────────────────

/** The network ids (D49). Every signed row, ref, entry and callback carries one. */
export const NETWORK_IDS = ['live', 'test', 'dev'] as const;
export type NetworkId = typeof NETWORK_IDS[number];

export function isNetworkId(value: unknown): value is NetworkId {
    return typeof value === 'string' && (NETWORK_IDS as readonly string[]).includes(value);
}

function requireNetwork(networkId: string): void {
    if (!isNetworkId(networkId)) throw new Error(`Unknown network id '${String(networkId)}': live, test or dev`);
}

/** A §4.21.1 decimal: ASCII digits, no leading zeros, `0` for zero. */
export const DECIMAL = /^(0|[1-9][0-9]*)$/;
/** A §4.21.1 signed decimal: a leading `-` on a negative value, never `+`, never `-0`. */
export const SIGNED_DECIMAL = /^(0|-?[1-9][0-9]*)$/;
/** A decimal from 1 (`era`, `seq`, `jseq`). */
export const POSITIVE_DECIMAL = /^[1-9][0-9]*$/;
/** 64 lowercase hex characters: a ref or a hash as the wire writes it. */
export const HEX64 = /^[0-9a-f]{64}$/;

// (rev 5) the encodings of the new fields (§4.21.1)
/** ISO 3166-1 alpha-2 in upper case, or empty when unknown (D56). Never folded: `nl` is malformed. */
export const COUNTRY_PATTERN = /^([A-Z]{2})?$/;
/** The paying object's kind (D56); empty off the payment rows. */
export const PAYER_KIND_PATTERN = /^(cash|demo|acct|acct-demo)?$/;
/** `content:<category>` or `service:<type>` (D55); empty off the payment rows. */
export const SERVICE_TYPE_PATTERN = /^((content|service):[a-z][a-z0-9-]{0,31})?$/;
/** A tax category (`RECORDS.taxCategories`: `standard`, `publication`), and the grammar of a bare
 *  service type (the key of `RECORDS.serviceTypes`; a row's `serviceType` is `service:` + it). */
export const CATEGORY_PATTERN = /^[a-z][a-z0-9-]{0,31}$/;
/** The content tiers (D54). */
export const TIERS = ['standard', 'premium', 'exclusive'] as const;
export type Tier = typeof TIERS[number];
/** A CPR AssetID or a sheet identifier; disjoint, as an AssetID never holds `-`. */
export const ASSET_ID_PATTERN = /^([a-z]{4}[0-9]{4}([a-z]{4}|[a-z]{2}[0-9]{2})|sheet-[a-z0-9-]{1,40})$/;
/** A UTC day on the wire and in exports (§4.21.1). */
export const DAY_PATTERN = /^[0-9]{4}-[0-9]{2}-[0-9]{2}$/;
/** The decimal of `clearedOnly`. */
export const CLEARED_ONLY_PATTERN = /^[01]$/;

const encoder = new TextEncoder();

/** UTF-8 bytes of a string. */
export function utf8(s: string): Uint8Array {
    return encoder.encode(s);
}

/** The decimal string of an integer given as a safe number, a bigint or a
 *  decimal string (checked against {@link SIGNED_DECIMAL}). */
export function decimal(value: number | bigint | string): string {
    if (typeof value === 'string') {
        if (!SIGNED_DECIMAL.test(value)) throw new Error(`Not a decimal integer: '${value}'`);
        return value;
    }
    if (typeof value === 'bigint') return value.toString(10);
    if (!Number.isSafeInteger(value)) throw new Error(`Not a safe integer: ${String(value)} (pass a bigint or a decimal string)`);
    return value.toString(10);
}

/**
 * `lp(x)`: the four-byte big-endian length of `x` followed by `x` (§4.21.1). A
 * string is its UTF-8 bytes, a number or bigint its decimal ASCII string, raw
 * bytes as given.
 */
export function lp(x: string | number | bigint | Uint8Array): Uint8Array {
    const body = x instanceof Uint8Array ? x
        : typeof x === 'string' ? utf8(x)
        : utf8(decimal(x));
    const out = new Uint8Array(4 + body.length);
    new DataView(out.buffer).setUint32(0, body.length, false);
    out.set(body, 4);
    return out;
}

/** The concatenation `‖` of byte strings. */
export function concat(...parts: Uint8Array[]): Uint8Array {
    let length = 0;
    for (const p of parts) length += p.length;
    const out = new Uint8Array(length);
    let at = 0;
    for (const p of parts) { out.set(p, at); at += p.length; }
    return out;
}

/** Lowercase hex. */
export function hex(bytes: Uint8Array): string {
    let s = '';
    for (const b of bytes) s += b.toString(16).padStart(2, '0');
    return s;
}

/** Bytes of an even-length hex string (either case). Throws on anything else. */
export function fromHex(value: string): Uint8Array {
    if (typeof value !== 'string' || value.length % 2 !== 0 || !/^[0-9a-fA-F]*$/.test(value)) {
        throw new Error('Not a hex string');
    }
    const out = new Uint8Array(value.length / 2);
    for (let i = 0; i < out.length; i++) out[i] = parseInt(value.slice(i * 2, i * 2 + 2), 16);
    return out;
}

const B64URL = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';

/** base64url, RFC 4648 §5, without padding. */
export function base64url(bytes: Uint8Array): string {
    let out = '';
    let i = 0;
    for (; i + 3 <= bytes.length; i += 3) {
        const n = (bytes[i] << 16) | (bytes[i + 1] << 8) | bytes[i + 2];
        out += B64URL[n >> 18] + B64URL[(n >> 12) & 63] + B64URL[(n >> 6) & 63] + B64URL[n & 63];
    }
    const rest = bytes.length - i;
    if (rest === 1) {
        const n = bytes[i] << 16;
        out += B64URL[n >> 18] + B64URL[(n >> 12) & 63];
    } else if (rest === 2) {
        const n = (bytes[i] << 16) | (bytes[i + 1] << 8);
        out += B64URL[n >> 18] + B64URL[(n >> 12) & 63] + B64URL[(n >> 6) & 63];
    }
    return out;
}

/** Bytes of an unpadded base64url string. Throws on padding, a foreign
 *  character, an impossible length or non-zero trailing bits. */
export function fromBase64url(value: string): Uint8Array {
    if (typeof value !== 'string' || value.length % 4 === 1) throw new Error('Not base64url');
    const out = new Uint8Array(Math.floor(value.length * 3 / 4));
    let acc = 0;
    let bits = 0;
    let at = 0;
    for (const c of value) {
        const v = B64URL.indexOf(c);
        if (v < 0) throw new Error('Not base64url');
        acc = ((acc << 6) | v) & 0xffff;
        bits += 6;
        if (bits >= 8) {
            bits -= 8;
            out[at++] = (acc >> bits) & 0xff;
        }
    }
    if ((acc & ((1 << bits) - 1)) !== 0) throw new Error('Not canonical base64url');
    return out;
}

const B32 = 'abcdefghijklmnopqrstuvwxyz234567';

/** base32, RFC 4648 alphabet in lowercase (`a–z2–7`), without padding. */
export function base32Lower(bytes: Uint8Array): string {
    let out = '';
    let acc = 0;
    let bits = 0;
    for (const b of bytes) {
        acc = ((acc << 8) | b) & 0xffff;
        bits += 8;
        while (bits >= 5) {
            bits -= 5;
            out += B32[(acc >> bits) & 31];
        }
    }
    if (bits > 0) out += B32[(acc << (5 - bits)) & 31];
    return out;
}

const DAY_MS = 86_400_000n;

/** The `YYYY-MM-DD` of a UTC day number (days since 1970-01-01), as captured
 *  tables keep it and the wire writes it (§4.21.1). */
export function dayString(day: string | number | bigint): string {
    const n = BigInt(decimal(day));
    if (n < 0n || n > 2_932_896n) throw new Error('A day number is 0 to 9999-12-31');
    const date = new Date(Number(n * DAY_MS));
    return `${String(date.getUTCFullYear()).padStart(4, '0')}-${String(date.getUTCMonth() + 1).padStart(2, '0')}`
        + `-${String(date.getUTCDate()).padStart(2, '0')}`;
}

/** The UTC day number of a `YYYY-MM-DD`, as a decimal string; refuses anything
 *  that is not a real day in that exact form. */
export function dayNumber(day: string): string {
    if (typeof day !== 'string' || !DAY_PATTERN.test(day)) throw new Error(`Not a day (YYYY-MM-DD): '${String(day)}'`);
    const ms = Date.UTC(Number(day.slice(0, 4)), Number(day.slice(5, 7)) - 1, Number(day.slice(8, 10)));
    const n = BigInt(ms) / DAY_MS;
    if (BigInt(ms) % DAY_MS !== 0n || n < 0n || dayString(n) !== day) throw new Error(`Not a day: '${day}'`);
    return n.toString();
}

// ── §4.21.3 view code, ref, role refs, site refs ────────────────────────────

/** The three issuer roles whose wallets carry a role ref; no code maps to them. */
export const ROLES = ['issuance', 'redemption', 'fee'] as const;
export type Role = typeof ROLES[number];

/** A valid code of any kind but VIEW, in any form, as its compact form. */
function requireViewable(code: string): string {
    const type = codeType(code);
    if (!type) throw new Error('Not a valid Bitcash code');
    if (type === TYPE_VIEW) throw new Error('The view code of a VIEW code is undefined');
    return toCompact(code);
}

/** `v = H(lp("bitcash-view-v2") ‖ lp(networkId) ‖ lp(compact(code)))`. */
export function viewDigest(code: string, networkId: NetworkId, sha256: Sha256): Uint8Array {
    requireNetwork(networkId);
    const compact = requireViewable(code);
    return sha256(concat(lp('bitcash-view-v2'), lp(networkId), lp(compact)));
}

/** `n = uint128_be(v[0..16]) >> 1`: the first 127 bits of `v`. */
export function viewNumber(digest: Uint8Array): bigint {
    if (!(digest instanceof Uint8Array) || digest.length < 16) throw new Error('A view digest is 32 bytes');
    let n = 0n;
    for (let i = 0; i < 16; i++) n = (n << 8n) | BigInt(digest[i]);
    return n >> 1n;
}

const BASE = BigInt(BODY_CHARS.length);

/** `n` written in base 55 over BODY_CHARS, 22 digits, most significant first. */
export function viewBody(n: bigint): string {
    if (typeof n !== 'bigint' || n < 0n || n >= 1n << 127n) throw new Error('A view number is 127 bits');
    const digits: string[] = [];
    for (let i = 0; i < BODY_LENGTH; i++) {
        digits.push(BODY_CHARS[Number(n % BASE)]);
        n /= BASE;
    }
    return digits.reverse().join('');
}

/** The view code of `code` (any valid form, any kind but VIEW) on `networkId`:
 *  a 24-character compact code of kind VIEW. */
export function viewCodeFor(code: string, networkId: NetworkId, sha256: Sha256): string {
    return composeCompact(viewBody(viewNumber(viewDigest(code, networkId, sha256))), TYPE_VIEW);
}

/** `ref = H(lp("bitcash-ref-v2") ‖ lp(networkId) ‖ lp(compact(viewCode)))`, 64
 *  lowercase hex. Refuses anything but a VIEW code. */
export function refForView(viewCode: string, networkId: NetworkId, sha256: Sha256): string {
    requireNetwork(networkId);
    if (codeType(viewCode) !== TYPE_VIEW) throw new Error('refForView takes a VIEW code');
    return hex(sha256(concat(lp('bitcash-ref-v2'), lp(networkId), lp(toCompact(viewCode)))));
}

/** The ref of any valid code: through its view code, or, for a VIEW code
 *  presented as a credential, directly (§4.21.3). */
export function refForCompact(code: string, networkId: NetworkId, sha256: Sha256): string {
    if (codeType(code) === TYPE_VIEW) return refForView(code, networkId, sha256);
    return refForView(viewCodeFor(code, networkId, sha256), networkId, sha256);
}

/** `roleRef = H(lp("bitcash-role-v2") ‖ lp(networkId) ‖ lp(role))`, 64 hex. */
export function roleRef(role: Role, networkId: NetworkId, sha256: Sha256): string {
    requireNetwork(networkId);
    if (!(ROLES as readonly string[]).includes(role)) throw new Error(`Unknown role '${String(role)}'`);
    return hex(sha256(concat(lp('bitcash-role-v2'), lp(networkId), lp(role))));
}

const ASCII_PRINTABLE = /^[\x21-\x7e]+$/;

/** `lower()` of §4.21.3: A–Z to a–z and nothing else. */
function asciiLower(s: string): string {
    return s.replace(/[A-Z]/g, c => String.fromCharCode(c.charCodeAt(0) + 32));
}

/**
 * `siteRef = H(lp("bitcash-site-v2") ‖ lp(networkId) ‖ lp(lower(domain)))`,
 * 64 lowercase hex (rev 5). `domain` is the A-label (punycode) form: this
 * library converts no U-label (a non-ASCII domain is refused) and lower-cases
 * ASCII A–Z only. It is the tally's site and a referrer's `referrerRef`;
 * never a channel's `via` and never a row's `counterpartyRef`.
 */
export function siteRef(networkId: NetworkId, domain: string, sha256: Sha256): string {
    requireNetwork(networkId);
    if (typeof domain !== 'string' || !ASCII_PRINTABLE.test(domain)) {
        throw new Error(`siteRef takes the A-label (ASCII) form of a domain: '${String(domain)}'`);
    }
    return hex(sha256(concat(lp('bitcash-site-v2'), lp(networkId), lp(asciiLower(domain)))));
}

/** A site tag's key: `H(lp(domainString) ‖ lp(networkId) ‖ lp(compact(code)))` for a code of `type`. */
function siteTagKey(domainString: string, type: 'DEMO' | 'AUTH', networkId: NetworkId, code: string, sha256: Sha256): Uint8Array {
    requireNetwork(networkId);
    if (typeof code !== 'string' || codeType(code) !== type) throw new Error(`This site tag is keyed by a ${type} code`);
    return sha256(concat(lp(domainString), lp(networkId), lp(toCompact(code))));
}

/** `hex(HMAC-SHA256(k, lp(domain)))`, the domain as {@link siteRef} takes it. */
function siteTagOf(k: Uint8Array, domain: string, hmacSha256: HmacSha256): string {
    if (typeof domain !== 'string' || !ASCII_PRINTABLE.test(domain)) {
        throw new Error(`A site tag takes the A-label (ASCII) form of a domain: '${String(domain)}'`);
    }
    return hex(hmacSha256(k, lp(asciiLower(domain))));
}

/**
 * The key of a DEMO wallet's site tag (§4.21.3; M4, decided 2026-10-02,
 * open-questions item 58): `k = H(lp("bitcash-demo-site-v2") ‖ lp(networkId) ‖
 * lp(compact(demoCode)))`. `demoCode` is a DEMO code in any form; any other
 * code is refused. The key exists only where the code is: the ledger never
 * stores it.
 */
export function demoSiteKey(networkId: NetworkId, demoCode: string, sha256: Sha256): Uint8Array {
    return siteTagKey('bitcash-demo-site-v2', 'DEMO', networkId, demoCode, sha256);
}

/**
 * `boundSiteTag = hex(HMAC-SHA256(k, lp(domain)))`, 64 lowercase hex: the one
 * site a DEMO wallet spends at, as its object stores it (§4.21.3). `domain`
 * as {@link siteRef} takes it: the A-label form (a non-ASCII domain is
 * refused), ASCII A–Z lower-cased. Without the demo's code nobody can test a
 * candidate domain against a tag.
 */
export function demoSiteTag(networkId: NetworkId, demoCode: string, domain: string, sha256: Sha256, hmacSha256: HmacSha256): string {
    return siteTagOf(demoSiteKey(networkId, demoCode, sha256), domain, hmacSha256);
}

/**
 * The key of a token's site-list tags (§4.21.3; M4, decided 2026-10-02,
 * open-questions item 59): `k = H(lp("bitcash-token-site-v2") ‖ lp(networkId)
 * ‖ lp(compact(authCode)))`, for an AUTH code in any form.
 */
export function tokenSiteKey(networkId: NetworkId, authCode: string, sha256: Sha256): Uint8Array {
    return siteTagKey('bitcash-token-site-v2', 'AUTH', networkId, authCode, sha256);
}

/**
 * One entry of a token's site list as the wallet stores it (D30, D67):
 * `hex(HMAC-SHA256(k, lp(domain)))` under {@link tokenSiteKey}, the domain as
 * {@link siteRef} takes it. A list holds tags only, never a domain.
 */
export function tokenSiteTag(networkId: NetworkId, authCode: string, domain: string, sha256: Sha256, hmacSha256: HmacSha256): string {
    return siteTagOf(tokenSiteKey(networkId, authCode, sha256), domain, hmacSha256);
}

// ── §4.21.10 keys and signing inputs ────────────────────────────────────────

/** The contexts under which SIGNING_SK signs (§4.21.10). `bitcash-usage-v2`
 *  (the usage export) and `bitcash-statement-v2` (the Operator statement) are
 *  reserved at F′ for track C. `bitcash-digest-v2` (the MAC tag inside the
 *  content URL digest) and `bitcash-history-v2` (the purchase history's
 *  derivation domain) are domains, not signature contexts. */
export const SIGNING_CONTEXTS = [
    'bitcash-row-v2', 'bitcash-segment-v2', 'bitcash-genesis-v2', 'bitcash-config-v2',
    'bitcash-supply-v2', 'bitcash-callback-v2', 'bitcash-attest-v2', 'bitcash-usage-v2', 'bitcash-statement-v2',
] as const;
export type SigningContext = typeof SIGNING_CONTEXTS[number];

/** `ctx ‖ "\0" ‖ payload`: what `Ed25519(SIGNING_SK, …)` signs. The payload is
 *  C for a row and `J(object without sig)` for a JSON object (./log.ts
 *  `jsonSigningInput`). */
export function signingInput(context: SigningContext, payload: Uint8Array): Uint8Array {
    if (!(SIGNING_CONTEXTS as readonly string[]).includes(context)) throw new Error(`Unknown signing context '${String(context)}'`);
    return concat(utf8(context), Uint8Array.of(0), payload);
}

/** `kid = hex(H(publicKey))[0..16]` over the 32 raw bytes of an Ed25519 key. */
export function kidOf(publicKey: Uint8Array, sha256: Sha256): string {
    if (!(publicKey instanceof Uint8Array) || publicKey.length !== 32) throw new Error('An Ed25519 public key is 32 bytes');
    return hex(sha256(publicKey)).slice(0, 16);
}

// ── §4.21.4 the canonical row string ────────────────────────────────────────

/** The 18 signed fields, in signing order (rev 5, D59: `operatorFee` and
 *  `earnedUnits` withdrawn, `country` and `payerKind` appended). */
export const ROW_FIELDS = [
    'version', 'network', 'era', 'kid', 'walletRef', 'seq', 'type', 'amount', 'balanceAfter', 'ts',
    'schemeFee', 'contentPath', 'serviceType', 'authRef', 'counterpartyRef', 'batchId', 'country', 'payerKind',
] as const;

/** A row's signed fields, each as the exact string that enters C. Numeric
 *  fields are always present (`0` when they do not apply); string fields are
 *  empty when absent. */
export interface SignedRowFields {
    /** The literal `v2`. */
    version: string;
    network: NetworkId | string;
    /** Decimal, from 1. */
    era: string;
    /** 16 lowercase hex. */
    kid: string;
    /** 64 hex: the row's own wallet. */
    walletRef: string;
    /** Decimal, from 1, contiguous per wallet. */
    seq: string;
    /** The registry grammar (§4.21.5): `[A-Za-z0-9_.:-]`, at most 128 characters. */
    type: string;
    /** Signed decimal units: `-` on a debit, `0` on a zero-amount row. */
    amount: string;
    /** Decimal units, never negative. */
    balanceAfter: string;
    /** Unix milliseconds, decimal. */
    ts: string;
    /** Decimal units, `0` when absent. */
    schemeFee: string;
    /** UTF-8; empty on content rows (D67) and on every row type the registry defines today. */
    contentPath: string;
    /** `content:<category>` or `service:<type>` on the payment row types; empty otherwise. */
    serviceType: string;
    /** 64 hex or empty: the AUTH token's ref on a token-routed row. */
    authRef: string;
    /** 64 hex or empty, per the registry; on a content row the site's Operator's wallet ref (D67). */
    counterpartyRef: string;
    /** 64 hex or empty: the batch hash of a settlement row, the session identifier of a session row. */
    batchId: string;
    /** The paying wallet's country on the payment row types (D56), upper case, or empty. */
    country: string;
    /** `cash`, `demo`, `acct` or `acct-demo` on the payment row types (D56); empty otherwise. */
    payerKind: string;
}

/** The charset and length of a row type. */
export const ROW_TYPE_CHARSET = /^[A-Za-z0-9_.:-]{1,128}$/;

const HEX_OR_EMPTY = /^([0-9a-f]{64})?$/;

function field(row: SignedRowFields, name: keyof SignedRowFields, pattern: RegExp | null): string {
    const value = row[name];
    if (typeof value !== 'string') throw new Error(`Row field ${name} must be a string`);
    if (pattern && !pattern.test(value)) throw new Error(`Row field ${name} is malformed: '${value}'`);
    return value;
}

const ROW_FIELD_RULES: Record<keyof SignedRowFields, RegExp | null> = {
    version: /^v2$/,
    network: /^(live|test|dev)$/,
    era: POSITIVE_DECIMAL,
    kid: /^[0-9a-f]{16}$/,
    walletRef: HEX64,
    seq: POSITIVE_DECIMAL,
    type: ROW_TYPE_CHARSET,
    amount: SIGNED_DECIMAL,
    balanceAfter: DECIMAL,
    ts: DECIMAL,
    schemeFee: DECIMAL,
    contentPath: null,
    serviceType: SERVICE_TYPE_PATTERN,
    authRef: HEX_OR_EMPTY,
    counterpartyRef: HEX_OR_EMPTY,
    batchId: HEX_OR_EMPTY,
    country: COUNTRY_PATTERN,
    payerKind: PAYER_KIND_PATTERN,
};

/** `C = lp(f1) ‖ … ‖ lp(f18)` (§4.21.4). Refuses a field that does not have
 *  its encoding, so nothing malformed is ever signed. The type is checked for
 *  charset and length here; {@link parseRowType} checks it against the
 *  registry and {@link rowRuleProblem} the registry's rules over the fields. */
export function canonicalRowString(row: SignedRowFields): Uint8Array {
    return concat(...ROW_FIELDS.map(name => lp(field(row, name, ROW_FIELD_RULES[name]))));
}

const ROW_DOMAIN = utf8('bitcash-row-v2\0');

/** `"bitcash-row-v2\0" ‖ C`: what the row signature signs. */
export function rowSigningInput(C: Uint8Array): Uint8Array {
    return concat(ROW_DOMAIN, C);
}

/** `rowLeafInput = "bitcash-row-v2\0" ‖ C ‖ sig` (§4.21.14), sig as its 64 raw
 *  bytes; the proof capability is `H(0x00 ‖ rowLeafInput)`. */
export function rowLeafInput(C: Uint8Array, sig: Uint8Array): Uint8Array {
    if (!(sig instanceof Uint8Array) || sig.length !== 64) throw new Error('A row signature is 64 bytes');
    return concat(ROW_DOMAIN, C, sig);
}

// ── §4.21.5 the row-type registry ───────────────────────────────────────────

/** A reference whose grammar §4.21.5 leaves open (`{paymentRef}`, §7 item 12;
 *  `{id}`): its family reserves the whole namespace, any suffix in the type
 *  charset, until the grammar is fixed. */
const OPEN_GRAMMAR = '[A-Za-z0-9_.:-]+';

const REF_GRAMMAR: Record<string, string> = {
    saleId: 's_[a-z2-7]{26}',
    batchId: '[0-9a-f]{64}',
    giftRef: '[0-9a-f]{64}',
    intentId: '[0-9a-f]{64}',
    toRefPrefix: '[0-9a-f]{12}',
    targetRefPrefix: '[0-9a-f]{12}',
    count: '0|[1-9][0-9]*',
    units: '0|[1-9][0-9]*',
    seq: '0|[1-9][0-9]*',
    reference: '[A-Za-z0-9_.-]{1,64}',
    reason: '[A-Za-z0-9_.-]{1,64}',
    invoiceRef: '[A-Za-z0-9_.-]{1,64}',
    operatorId: '[a-z0-9]{2,4}',
    event: 'fund|auth',
    txhash: '[0-9a-f]+',
    paymentRef: OPEN_GRAMMAR,
    id: OPEN_GRAMMAR,
};

/** One registry type: its template (the `family`), the grammar it compiles to,
 *  the references it names, and whether it is reserved (not written yet). */
export interface RowTypeSpec {
    readonly family: string;
    readonly pattern: RegExp;
    readonly refs: readonly string[];
    readonly reserved: boolean;
}

function spec(family: string, reserved = false): RowTypeSpec {
    const refs: string[] = [];
    const source = family.split(/(\{[a-zA-Z]+\})/).map(part => {
        const m = /^\{([a-zA-Z]+)\}$/.exec(part);
        if (!m) return part.replace(/[.*+?^${}()|[\]\\-]/g, '\\$&');
        const grammar = REF_GRAMMAR[m[1]];
        if (!grammar) throw new Error(`No grammar for {${m[1]}}`);
        refs.push(m[1]);
        return `(${grammar})`;
    }).join('');
    return { family, pattern: new RegExp(`^${source}$`), refs, reserved };
}

/** The registry of §4.21.5 as data, one entry per row type. A type no entry
 *  matches is unregistered, which is a bug. (rev 5) Retired, and so refused:
 *  `payout:{reference}`, `fork-settlement`, `publisher-share`, `fee:fork`,
 *  `fee:fork-forced` (D52) and `content-payment` (D64); reserved until M15: the
 *  account types. */
export const ROW_TYPES: readonly RowTypeSpec[] = [
    spec('mint:{reference}'), spec('mint:inventory:{saleId}'), spec('mint:sale:{saleId}', true),
    spec('self-issue:{reference}'), spec('grant:{reference}'), spec('sale:{saleId}'),
    spec('inventory:{saleId}'), spec('inventory-settled:{invoiceRef}:{units}'),
    spec('service-payment'), spec('content-tab:{count}'), spec('service-tab:{count}'),
    spec('settlement:{batchId}'), spec('transfer'), spec('gift-create'), spec('gift-redeem'),
    spec('gift-reclaim:{giftRef}'), spec('demo-create'), spec('lock-create'), spec('comp:{reference}'),
    spec('lock-release:{targetRefPrefix}'), spec('lock-release'), spec('recall:{saleId}'),
    spec('reversal:{saleId}'), spec('bounce:{toRefPrefix}'), spec('bounce:{intentId}'),
    spec('credit-back:{intentId}'), spec('fee:{event}'), spec('fee:settle'), spec('sweep:fees'),
    spec('expiry-return'), spec('redemption:{invoiceRef}'), spec('admin:{reason}'), spec('freeze:{reason}'),
    spec('unfreeze'), spec('repair:{seq}', true), spec('bridge-deposit:{txhash}', true),
    spec('bridge-withdraw:pending:{id}', true), spec('bridge-withdraw:{txhash}', true),
    spec('deposit:{invoiceRef}', true), spec('advance:{paymentRef}', true),
    spec('advance-settled:{invoiceRef}:{units}', true), spec('account-return', true),
];

export interface ParsedRowType {
    /** The registry template, e.g. `bounce:{intentId}`. */
    family: string;
    /** The references the type carries, by name. */
    refs: Record<string, string>;
    reserved: boolean;
}

/** The registry entry a row type belongs to, with its references, or null for
 *  a type outside the charset, longer than 128 characters or unregistered.
 *  The grammars are disjoint, so at most one entry matches. */
export function parseRowType(type: string): ParsedRowType | null {
    if (typeof type !== 'string' || !ROW_TYPE_CHARSET.test(type)) return null;
    for (const t of ROW_TYPES) {
        const m = t.pattern.exec(type);
        if (!m) continue;
        const refs: Record<string, string> = {};
        t.refs.forEach((name, i) => { refs[name] = m[i + 1]; });
        return { family: t.family, refs, reserved: t.reserved };
    }
    return null;
}

/** The payment row types (§4.21.5) and the `serviceType` family each carries:
 *  they, and only they, carry `serviceType`, `country` and `payerKind`. */
export const PAYMENT_ROW_FAMILIES: Readonly<Record<string, 'content:' | 'service:'>> = {
    'service-payment': 'service:',
    'content-tab:{count}': 'content:',
    'service-tab:{count}': 'service:',
};

const SESSION_FAMILIES: ReadonlySet<string> = new Set(['content-tab:{count}', 'service-tab:{count}']);

/**
 * The first registry rule a row breaks whose fields have their encodings
 * (§4.21.4, §4.21.5), as a text that starts with the field's name; null when
 * it breaks none:
 *   type         registered (a retired type is not);
 *   contentPath  empty on every type the registry defines today (D67);
 *   serviceType  on the payment row types `content:` (content-tab) or
 *                `service:` (service-payment, service-tab), never empty;
 *   payerKind    on the payment row types, never empty;
 *   serviceType, country, payerKind   empty on every other type;
 *   batchId      a settlement's own batch hash; non-empty on a session row
 *                (its tabId); empty on every other type.
 * Which wallet a `counterpartyRef` names is the verifier's (M11 `ROW_FIELD`).
 */
export function rowRuleProblem(row: SignedRowFields): string | null {
    const parsed = parseRowType(row.type);
    if (parsed === null) return `type '${row.type}' is not in the registry`;
    if (row.contentPath !== '') return 'contentPath is empty on every registered row type';
    const family = parsed.family;
    const prefix = PAYMENT_ROW_FAMILIES[family];
    if (prefix !== undefined) {
        if (!row.serviceType.startsWith(prefix) || !SERVICE_TYPE_PATTERN.test(row.serviceType)) {
            return `serviceType of a ${family} row is ${prefix}<…>`;
        }
        if (row.payerKind === '') return `payerKind is set on a ${family} row`;
    } else {
        for (const name of ['serviceType', 'country', 'payerKind'] as const) {
            if (row[name] !== '') return `${name} is empty on a ${family} row`;
        }
    }
    if (family === 'settlement:{batchId}') {
        if (row.batchId !== parsed.refs.batchId) return 'batchId of a settlement row is its own batch hash';
    } else if (SESSION_FAMILIES.has(family)) {
        if (row.batchId === '') return 'batchId of a session row is its tabId';
    } else if (row.batchId !== '') {
        return `batchId is empty on a ${family} row`;
    }
    return null;
}

// ── §4.21.8 intent keys ─────────────────────────────────────────────────────

/** `ik = H(lp("bitcash-ik-v2") ‖ lp(senderName) ‖ lp(jseq) ‖ lp(leg))`: the
 *  key of a one-shot intent. `jseq` the producing entry's, from 1; `leg` the
 *  intent's index among that entry's intents, from 0. */
export function ikOneShot(senderName: string, jseq: string | number | bigint, leg: string | number | bigint,
    sha256: Sha256): Uint8Array {
    const j = decimal(jseq);
    const l = decimal(leg);
    if (!POSITIVE_DECIMAL.test(j)) throw new Error('jseq is a decimal from 1');
    if (!DECIMAL.test(l)) throw new Error('leg is a decimal from 0');
    if (!senderName) throw new Error('An intent names its sender');
    return sha256(concat(lp('bitcash-ik-v2'), lp(senderName), lp(j), lp(l)));
}

export type EdgeKind = 'reverse' | 'reverse-result';

/** `ik = H(lp("bitcash-ik-edge-v2") ‖ lp(senderName) ‖ lp(kind) ‖ lp(saleId) ‖
 *  lp(toName))`: the key of a reversal-walk edge. */
export function ikEdge(senderName: string, kind: EdgeKind, saleId: string, toName: string, sha256: Sha256): Uint8Array {
    if (kind !== 'reverse' && kind !== 'reverse-result') throw new Error(`Unknown edge kind '${String(kind)}'`);
    if (!SALE_ID_PATTERN.test(saleId)) throw new Error('Not a saleId');
    if (!senderName || !toName) throw new Error('An edge names both ends');
    return sha256(concat(lp('bitcash-ik-edge-v2'), lp(senderName), lp(kind), lp(saleId), lp(toName)));
}

/** `intentId = hex(ik)`. */
export function intentIdOf(ik: Uint8Array): string {
    if (!(ik instanceof Uint8Array) || ik.length !== 32) throw new Error('An intent key is 32 bytes');
    return hex(ik);
}

// ── §4.21.9 sale and order identifiers ──────────────────────────────────────

export const SALE_ID_PATTERN = /^s_[a-z2-7]{26}$/;
export const ORDER_ID_PATTERN = /^o_[a-z2-7]{26}$/;
export const CALLBACK_REF_PATTERN = /^[A-Za-z0-9_-]{1,64}$/;
/** An `operatorId` is the Operator's operator code: two to four lowercase ASCII letters or digits (the shape of
 *  libs/federation/vectors/operator-codes.json, which format-pack.test.ts reads; this library takes no dependency on
 *  libs/federation). The shape only: the trust root never grants a reserved code, so none reaches the ledger. */
export const OPERATOR_ID_PATTERN = /^[a-z0-9]{2,4}$/;

function saleId(networkId: NetworkId, operatorId: string, kind: 'keyed' | 'link', x: string | Uint8Array,
    sha256: Sha256): string {
    requireNetwork(networkId);
    if (!OPERATOR_ID_PATTERN.test(operatorId)) throw new Error(`Not an operatorId (an operator code, [a-z0-9]{2,4}): '${operatorId}'`);
    return 's_' + base32Lower(sha256(concat(lp('bitcash-sale-v2'), lp(networkId), lp(operatorId), lp(kind), lp(x))))
        .slice(0, 26);
}

/** A keyed sale's id: `x` = the callbackRef's UTF-8 bytes. */
export function saleIdKeyed(networkId: NetworkId, operatorId: string, callbackRef: string, sha256: Sha256): string {
    if (!CALLBACK_REF_PATTERN.test(callbackRef)) throw new Error(`Not a callbackRef: '${callbackRef}'`);
    return saleId(networkId, operatorId, 'keyed', callbackRef, sha256);
}

/** A link sale's id: `x` = sessionHash, the 32 raw bytes of H(sessionId). */
export function saleIdLink(networkId: NetworkId, operatorId: string, sessionHash: Uint8Array, sha256: Sha256): string {
    if (!(sessionHash instanceof Uint8Array) || sessionHash.length !== 32) throw new Error('A sessionHash is 32 bytes');
    return saleId(networkId, operatorId, 'link', sessionHash, sha256);
}

/** `sessionHash = H(sessionId)`, 32 bytes. */
export function sessionHashOf(sessionId: string, sha256: Sha256): Uint8Array {
    return sha256(utf8(sessionId));
}

/** `piHash = H(paymentIntentId)`, 32 bytes. */
export function piHashOf(paymentIntentId: string, sha256: Sha256): Uint8Array {
    return sha256(utf8(paymentIntentId));
}

/** `orderId = "o_" + base32(16 random bytes)[0..26]`; the caller supplies the
 *  bytes from a CSPRNG. */
export function orderId(random16: Uint8Array): string {
    if (!(random16 instanceof Uint8Array) || random16.length !== 16) throw new Error('An orderId takes 16 random bytes');
    return 'o_' + base32Lower(random16).slice(0, 26);
}

// ── §4.21.16 batchId ────────────────────────────────────────────────────────

/** One batch line (§4.21.16, fees §3.9): `from` the payer's or token's ref,
 *  `via` the receive-address ref paid to or empty, `kind` 0 (a service payment
 *  to an Operator wallet), 2 (the scheme fee) or 3 (content to the site's
 *  Operator); the channel's tax `category` (empty on kind 2) and the payer's
 *  `country` and `payerKind`. */
export interface BatchLine {
    from: string;
    via: string;
    kind: string | number;
    category: string;
    country: string;
    payerKind: string;
    epoch: string | number | bigint;
    seq: string | number | bigint;
    units: string | number | bigint;
}

function cmp(a: string, b: string): number {
    return a < b ? -1 : a > b ? 1 : 0;
}

/** The lines in `(from, via, kind, category)` order, each compared as its
 *  string (code-unit order; an empty string first). */
export function sortBatchLines<T extends BatchLine>(lines: readonly T[]): T[] {
    return [...lines].sort((a, b) => cmp(a.from, b.from) || cmp(a.via, b.via)
        || cmp(decimal(a.kind), decimal(b.kind)) || cmp(a.category, b.category));
}

/** The line's shape, as the payee checks a state (fees §3.7): kinds 0, 2, 3;
 *  a kind-3 line names no `via`; a kind-2 line neither a `via` nor a category;
 *  a kind-0 or kind-3 line a category. */
function batchLineFields(l: BatchLine): string[] {
    if (!HEX64.test(l.from) || !HEX_OR_EMPTY.test(l.via)) throw new Error('A batch line names refs in lowercase hex');
    const kind = decimal(l.kind);
    if (kind !== '0' && kind !== '2' && kind !== '3') throw new Error(`Channel kind ${kind}: kinds are 0, 2 and 3`);
    if (kind === '3' && l.via !== '') throw new Error('A kind-3 line names no via: content channels are per Operator (D52)');
    if (kind === '2') {
        if (l.via !== '' || l.category !== '') throw new Error('A kind-2 line has an empty via and category: one fee channel per payer');
    } else if (!CATEGORY_PATTERN.test(l.category)) {
        throw new Error(`A kind-${kind} line names its category: '${l.category}'`);
    }
    if (!COUNTRY_PATTERN.test(l.country)) throw new Error(`country is two upper-case letters or empty: '${l.country}'`);
    if (!PAYER_KIND_PATTERN.test(l.payerKind)) throw new Error(`Unknown payer kind '${l.payerKind}'`);
    const epoch = decimal(l.epoch);
    const seq = decimal(l.seq);
    const units = decimal(l.units);
    for (const n of [epoch, seq, units]) if (!DECIMAL.test(n)) throw new Error('epoch, seq and units are decimals');
    return [l.from, l.via, kind, l.category, l.country, l.payerKind, epoch, seq, units];
}

/** The exact bytes `batchId` hashes. */
export function batchIdPreimage(networkId: NetworkId, walletRef: string, batchNo: string | number | bigint,
    lines: readonly BatchLine[]): Uint8Array {
    requireNetwork(networkId);
    if (!HEX64.test(walletRef)) throw new Error('walletRef is 64 lowercase hex');
    const no = decimal(batchNo);
    if (!DECIMAL.test(no)) throw new Error('batchNo is a decimal');
    const parts: Uint8Array[] = [lp('bitcash-batch-v2'), lp(networkId), lp(walletRef), lp(no)];
    for (const l of sortBatchLines(lines)) for (const f of batchLineFields(l)) parts.push(lp(f));
    return concat(...parts);
}

/** `batchId = H(lp("bitcash-batch-v2") ‖ lp(network) ‖ lp(walletRef) ‖ lp(batchNo)
 *  ‖ for each line, in (from, via, kind, category) order: lp(from) ‖ lp(via) ‖
 *  lp(kind) ‖ lp(category) ‖ lp(country) ‖ lp(payerKind) ‖ lp(epoch) ‖ lp(seq) ‖
 *  lp(units))` (§4.21.16). Per-asset data never enters it. */
export function batchId(networkId: NetworkId, walletRef: string, batchNo: string | number | bigint,
    lines: readonly BatchLine[], sha256: Sha256): Uint8Array {
    return sha256(batchIdPreimage(networkId, walletRef, batchNo, lines));
}

// ── §4.21.16 the session identifier ─────────────────────────────────────────

/** The exact bytes `tabId` hashes. `scope` is the Operator's wallet ref (64
 *  lowercase hex) for a content session, or the bare service type (the key of
 *  `RECORDS.serviceTypes`, `[a-z][a-z0-9-]{0,31}`; a row's `serviceType` is
 *  `service:` + it) for a service session; `clearedOnly` 0 or 1. */
export function tabIdPreimage(networkId: NetworkId, walletRef: string, tabNo: string | number | bigint, scope: string,
    category: string, clearedOnly: string | number): Uint8Array {
    requireNetwork(networkId);
    if (!HEX64.test(walletRef)) throw new Error('walletRef is 64 lowercase hex');
    const no = decimal(tabNo);
    if (!DECIMAL.test(no)) throw new Error('tabNo is a decimal');
    if (!HEX64.test(scope) && !CATEGORY_PATTERN.test(scope)) {
        throw new Error(`scope is the Operator's wallet ref or a bare service type: '${scope}'`);
    }
    if (!CATEGORY_PATTERN.test(category)) throw new Error(`A session names its category: '${category}'`);
    const cleared = decimal(clearedOnly);
    if (!CLEARED_ONLY_PATTERN.test(cleared)) throw new Error('clearedOnly is 0 or 1');
    return concat(lp('bitcash-tab-v2'), lp(networkId), lp(walletRef), lp(no), lp(scope), lp(category), lp(cleared));
}

/** `tabId = H(lp("bitcash-tab-v2") ‖ lp(network) ‖ lp(walletRef) ‖ lp(tabNo) ‖
 *  lp(scope) ‖ lp(category) ‖ lp(clearedOnly))`, fixed when the session opens:
 *  its row's `batchId`. Nothing a unit adds enters it (decided 2026-10-02). */
export function tabId(networkId: NetworkId, walletRef: string, tabNo: string | number | bigint, scope: string,
    category: string, clearedOnly: string | number, sha256: Sha256): Uint8Array {
    return sha256(tabIdPreimage(networkId, walletRef, tabNo, scope, category, clearedOnly));
}

// ── §4.21.11 the request envelope ───────────────────────────────────────────

export interface EnvelopeFields {
    networkId: NetworkId;
    /** The caller's registered key id, 16 hex. */
    kid: string;
    /** Uppercase, as sent. */
    method: string;
    /** Lowercase as sent, with the port in dev. */
    host: string;
    pathWithQuery: string;
    /** Unix ms, decimal. */
    ts: string | number | bigint;
    /** 16 random bytes, base64url, as sent in `X-Bitcash-Nonce`. */
    nonce: string;
    /** The request body: bytes, or a string taken as its UTF-8 bytes; empty for none. */
    body: Uint8Array | string;
}

/** `hex(H(body))`. */
export function envelopeBodyHash(body: Uint8Array | string, sha256: Sha256): string {
    return hex(sha256(typeof body === 'string' ? utf8(body) : body));
}

/** `m = lp("bitcash-req-v1") ‖ lp(networkId) ‖ lp(kid) ‖ lp(METHOD) ‖ lp(host)
 *  ‖ lp(pathWithQuery) ‖ lp(ts) ‖ lp(nonce) ‖ lp(hex(H(body)))`, what
 *  `X-Bitcash-Signature` signs (Ed25519, no context prefix: the domain string
 *  leads `m`). */
export function envelopeMessage(f: EnvelopeFields, sha256: Sha256): Uint8Array {
    requireNetwork(f.networkId);
    if (!/^[0-9a-f]{16}$/.test(f.kid)) throw new Error('kid is 16 lowercase hex');
    if (!/^[A-Z]+$/.test(f.method)) throw new Error('METHOD is uppercase');
    if (!f.host || f.host !== f.host.toLowerCase()) throw new Error('host is lowercase');
    if (fromBase64url(f.nonce).length !== 16) throw new Error('The nonce is 16 bytes, base64url');
    return concat(lp('bitcash-req-v1'), lp(f.networkId), lp(f.kid), lp(f.method), lp(f.host), lp(f.pathWithQuery),
        lp(decimal(f.ts)), lp(f.nonce), lp(envelopeBodyHash(f.body, sha256)));
}

// ── §4.21.13 content secrets ────────────────────────────────────────────────

export const CONTENT_PURPOSES = ['content-digest', 'origin-fetch'] as const;
export type ContentPurpose = typeof CONTENT_PURPOSES[number];

/** HKDF-SHA256 (RFC 5869) over an injected HMAC-SHA256. */
export function hkdfSha256(ikm: Uint8Array, salt: Uint8Array, info: Uint8Array, length: number,
    hmacSha256: HmacSha256): Uint8Array {
    if (!Number.isInteger(length) || length < 1 || length > 255 * 32) throw new Error('HKDF length out of range');
    const prk = hmacSha256(salt, ikm);
    const out = new Uint8Array(length);
    let t: Uint8Array = new Uint8Array(0);
    for (let i = 1, at = 0; at < length; i++) {
        t = hmacSha256(prk, concat(t, info, Uint8Array.of(i)));
        out.set(t.subarray(0, Math.min(t.length, length - at)), at);
        at += t.length;
    }
    return out;
}

/** `secret(purpose, domain, epoch) = HKDF-SHA256(ikm = CONTENT_MASTER, salt =
 *  empty, info = lp("bitcash-content-v2") ‖ lp(purpose) ‖ lp(domain) ‖
 *  lp(epoch), L = 32)`. `domain` lowercase, `epoch` the site record's
 *  `secretEpoch`. */
export function contentSecret(master: Uint8Array, purpose: ContentPurpose, domain: string,
    epoch: string | number | bigint, hmacSha256: HmacSha256): Uint8Array {
    if (!(master instanceof Uint8Array) || master.length !== 32) throw new Error('CONTENT_MASTER is 32 bytes');
    if (!(CONTENT_PURPOSES as readonly string[]).includes(purpose)) throw new Error(`Unknown purpose '${String(purpose)}'`);
    if (!domain || domain !== domain.toLowerCase()) throw new Error('The domain is lowercase');
    const info = concat(lp('bitcash-content-v2'), lp(purpose), lp(domain), lp(decimal(epoch)));
    return hkdfSha256(master, new Uint8Array(0), info, 32, hmacSha256);
}

// ── §4.21.13 the content URL digest (rev 5; D54, D66) ───────────────────────

/** What the digest signs. `domain` the origin site's, A-label, lower case;
 *  `contentPath` the unit's path on its origin site after `/{domain}` in the
 *  content route: a leading `/`, the file name included, no query, as the
 *  WHATWG URL serialization writes it (`new URL(href).pathname`, uppercase
 *  percent-escapes), never decoded; `price` whole ledger units; `rev` from 1. */
export interface ContentDigestFields {
    domain: string;
    contentPath: string;
    assetId: string;
    category: string;
    tier: string;
    price: string;
    rev: string;
}

/** The URL parameters, in the order the publication software writes them; the
 *  ledger reads them by name. The bearer's `authToken` or `demo` follows,
 *  outside the digest; the referrer is never in the URL (D65). */
export const CONTENT_URL_PARAMS = ['assetId', 'category', 'tier', 'price', 'rev', 'digest'] as const;

/** 0x21–0x7E less the characters the WHATWG path serialization escapes (`"` `#` `<` `>` `?` `` ` `` `{` `}`). */
const CONTENT_PATH = /^\/[!$-;=@-_a-z|~]*$/;

function digestFields(f: ContentDigestFields): string[] {
    if (typeof f.domain !== 'string' || !ASCII_PRINTABLE.test(f.domain) || f.domain !== asciiLower(f.domain)) {
        throw new Error(`The domain is the lower-case A-label form: '${String(f.domain)}'`);
    }
    if (typeof f.contentPath !== 'string' || !CONTENT_PATH.test(f.contentPath)) {
        throw new Error(`contentPath is the serialized URL path, from '/': '${String(f.contentPath)}'`);
    }
    if (!ASSET_ID_PATTERN.test(f.assetId)) throw new Error(`Not an assetId: '${f.assetId}'`);
    if (!CATEGORY_PATTERN.test(f.category)) throw new Error(`Not a category: '${f.category}'`);
    if (!(TIERS as readonly string[]).includes(f.tier)) throw new Error(`Not a tier: '${f.tier}'`);
    if (typeof f.price !== 'string' || !DECIMAL.test(f.price)) throw new Error('price is whole ledger units, a decimal');
    if (typeof f.rev !== 'string' || !POSITIVE_DECIMAL.test(f.rev)) throw new Error('rev is a decimal from 1');
    return [f.domain, f.contentPath, f.assetId, f.category, f.tier, f.price, f.rev];
}

/** `msg = lp("bitcash-digest-v2") ‖ lp(domain) ‖ lp(contentPath) ‖ lp(assetId)
 *  ‖ lp(category) ‖ lp(tier) ‖ lp(price) ‖ lp(rev)`. */
export function contentDigestMessage(fields: ContentDigestFields): Uint8Array {
    return concat(lp('bitcash-digest-v2'), ...digestFields(fields).map(f => lp(f)));
}

/** `mac = HMAC-SHA256(key, msg)`, key the 32 raw bytes of
 *  `secret("content-digest", domain, epoch)` ({@link contentSecret}); the URL
 *  carries it as base64url without padding. */
export function contentDigest(key: Uint8Array, fields: ContentDigestFields, hmacSha256: HmacSha256): Uint8Array {
    if (!(key instanceof Uint8Array) || key.length !== 32) throw new Error('The content-digest key is 32 bytes');
    return hmacSha256(key, contentDigestMessage(fields));
}

/** The signed URL's query: the parameters of {@link CONTENT_URL_PARAMS} in
 *  order; every value is URL-safe as it stands. */
export function contentUrlQuery(fields: ContentDigestFields, mac: Uint8Array): string {
    const [, , assetId, category, tier, price, rev] = digestFields(fields);
    if (!(mac instanceof Uint8Array) || mac.length !== 32) throw new Error('A content digest is 32 bytes');
    const values = [assetId, category, tier, price, rev, base64url(mac)];
    return CONTENT_URL_PARAMS.map((name, i) => `${name}=${values[i]}`).join('&');
}
